<!--
  A plain-text field with insertable, self-documenting {token} variables,
  instead of an admin having to remember/type the right curly-brace name by
  hand -- that's already caused a real bug once (a UI hint showed
  {contact_name} where the actually-resolvable token was {caller_name}), and
  it's the same fragile pattern across every dynamic-text field in the
  product (greeting/mission prompt/Voice Blast script/slot-booking
  confirmation/org messages/etc), not just one screen. Click a variable to
  insert it at the cursor; a live preview below shows the resolved example
  value so "what does this variable actually point to" is never a guessing
  game either. 2026-09-05, generalized out of the chip-insertion idea
  already proven in OnCallEmailTemplateEditor.vue/notificationTemplateEditor.js
  -- that one manages rich contenteditable HTML (email templates need real
  markup); every field this component targets is a plain string, so it stays
  a plain <textarea> with literal {token} substrings, which is also exactly
  what every existing backend resolver (resolvePlaceholders/applyTokens/etc)
  already expects to find in the stored value -- no format change, just how
  the text gets typed.
-->
<template>
  <div class="vit">
    <div v-if="variables.length" class="vit__toolbar">
      <span class="vit__toolbar-label">Insert variable:</span>
      <button
        v-for="v in variables"
        :key="v.token"
        type="button"
        class="vit__chip"
        :disabled="disabled"
        :title="v.example ? `e.g. ${v.example}` : ''"
        @click="insert(v.token)"
      >
        {{ v.label }}
      </button>
    </div>

    <textarea
      ref="textareaEl"
      :value="modelValue"
      class="tw-textarea"
      :rows="rows"
      :placeholder="placeholder"
      :maxlength="maxlength || undefined"
      :disabled="disabled"
      @input="onInput"
    />

    <p v-if="showPreview && String(modelValue || '').trim()" class="vit__preview">
      <span class="vit__preview-label">Preview:</span> {{ previewText }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

defineOptions({ name: 'VariableInsertTextarea' })

const props = defineProps({
  modelValue: { type: String, default: '' },
  // [{ token: 'caller_name', label: 'Caller name', example: 'John Doe' }, ...]
  // -- the only thing that differs per screen; the component itself knows
  // nothing about what a "caller_name" or "resource_name" actually is, or
  // which backend resolver will end up reading it.
  variables: { type: Array, default: () => [] },
  rows: { type: [Number, String], default: 3 },
  placeholder: { type: String, default: '' },
  maxlength: { type: [Number, String], default: null },
  disabled: { type: Boolean, default: false },
  showPreview: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue'])

const textareaEl = ref(null)

function onInput(event) {
  emit('update:modelValue', event.target.value)
}

function insert(token) {
  const el = textareaEl.value
  const value = props.modelValue || ''
  if (!el) {
    emit('update:modelValue', `${value}{${token}}`)
    return
  }
  const start = el.selectionStart ?? value.length
  const end = el.selectionEnd ?? value.length
  const next = `${value.slice(0, start)}{${token}}${value.slice(end)}`
  emit('update:modelValue', next)

  // Caret lands right after the inserted token, same as a real text editor
  // would leave it -- next frame, so the DOM has the new value first.
  const caretPos = start + token.length + 2
  requestAnimationFrame(() => {
    el.focus()
    el.setSelectionRange(caretPos, caretPos)
  })
}

// Same {token} substitution rule the backend resolvers actually apply --
// an unresolved token is left visible here too, deliberately, so the
// preview never lies about what will happen to a token this screen
// doesn't actually support.
const previewText = computed(() => {
  const exampleByToken = Object.fromEntries(props.variables.map((v) => [v.token, v.example]))
  return String(props.modelValue || '').replace(/\{([a-zA-Z0-9_.]+)\}/g, (match, token) => (
    exampleByToken[token] !== undefined && exampleByToken[token] !== ''
      ? exampleByToken[token]
      : match
  ))
})
</script>

<style scoped>
.vit {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.vit__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.vit__toolbar-label {
  font-size: 0.76rem;
  opacity: 0.65;
  margin-right: 0.15rem;
}

.vit__chip {
  font-size: 0.76rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(99, 102, 241, 0.08);
  color: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.vit__chip:hover:not(:disabled) {
  background: rgba(99, 102, 241, 0.16);
  border-color: rgba(99, 102, 241, 0.5);
}

.vit__chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vit__preview {
  font-size: 0.78rem;
  opacity: 0.75;
  margin: 0;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(148, 163, 184, 0.1);
}

.vit__preview-label {
  font-weight: 600;
  opacity: 0.85;
}
</style>
